import { monthRange, fetchPaged } from "./shared";

const dnAmount = (item) =>
  Number(item?.saleNetAmount ?? item?.saleTotalAmount ?? item?.amount ?? 0) || 0;

const getInvoiceMade = (item) => {
  const raw = item?.invoiceMade ?? item?.attributes?.invoiceMade;
  if (raw === true || raw === "true") return 1;
  if (raw === false || raw === "false") return 0;
  const num = Number(raw);
  if (Number.isNaN(num)) return null;
  return num;
};

const isDnInvoiced = (item) => getInvoiceMade(item) === 1;
const isDnNonInvoiced = (item) => getInvoiceMade(item) === 0;

export async function fetchDeliveryNotesLive({
  from,
  to,
  invoiceMade,
  plateNumber,
  limit,
  maxPages,
} = {}) {
  const encodedPlate = plateNumber ? encodeURIComponent(plateNumber) : "";
  return fetchPaged(
    (page, pageSize) => {
      const params = [`page=${page}`, `limit=${pageSize}`, "valid=true"];
      if (plateNumber) {
        params.push("deliveryNoteNumber=");
        params.push("deliveryNoteRefNumber=");
        params.push(`plateNumber=${encodedPlate}`);
        params.push("stationCity=");
        params.push("invoiceMade=1");
        params.push("invoiceMade=0");
      } else {
        if (from) params.push(`from=${encodeURIComponent(from)}`);
        if (to) params.push(`to=${encodeURIComponent(to)}`);
        const madeValues = Array.isArray(invoiceMade)
          ? invoiceMade
          : invoiceMade === 0 || invoiceMade === 1
            ? [invoiceMade]
            : [];
        madeValues.forEach((value) => params.push(`invoiceMade=${value}`));
      }
      return `/api/deliverynotes/get?${params.join("&")}`;
    },
    {
      limit: limit || (plateNumber ? 100 : 50),
      maxPages: maxPages || (plateNumber ? 3 : 6),
    }
  );
}

export async function fetchDeliveryNotesByPlate(plateNumber) {
  if (!plateNumber) return [];
  return fetchDeliveryNotesLive({ plateNumber });
}

export const mapVehicleDeliveryNote = (item) => ({
  id: item.id,
  deliveryNoteNumber: item.deliveryNoteNumber || item.id,
  deliveryNoteRefNumber: item.deliveryNoteRefNumber || "",
  stationName: item.stationName || "",
  workOrderNumber: item.workOrderNumber || "",
  plateNumber: item.plateNumber || "",
  serviceDate: item.serviceDate || item.createdTime,
  amount: dnAmount(item),
  invoiceMade: getInvoiceMade(item),
  invoiced: getInvoiceMade(item) === 1,
  odometer: item.odometerReading ?? item.odometer,
  accountName: item.attributes?.c_accountName || item.accountName || "",
});

export const summarizeDeliveryNotes = (rows) => {
  const invoiced = rows.filter(isDnInvoiced);
  const nonInvoiced = rows.filter(isDnNonInvoiced);
  const totalAmount = rows.reduce((sum, item) => sum + dnAmount(item), 0);
  const invoicedAmount = invoiced.reduce((sum, item) => sum + dnAmount(item), 0);
  const nonInvoicedAmount = nonInvoiced.reduce(
    (sum, item) => sum + dnAmount(item),
    0
  );
  return {
    totalDeliveryNotes: rows.length,
    totalAmount,
    invoicedAmount,
    nonInvoicedAmount,
    pendingCount: nonInvoiced.length,
  };
};

export async function loadDeliveryNotesQuestion(questionId) {
  switch (questionId) {
    case "dnCurrentMonth": {
      const current = monthRange(0);
      const currentRows = await fetchDeliveryNotesLive({
        ...current,
        limit: 1000,
        maxPages: 1,
      });
      return {
        summary: {
          currentMonth: {
            label: current.label,
            items: currentRows.map(mapVehicleDeliveryNote),
            ...summarizeDeliveryNotes(currentRows),
          },
        },
      };
    }

    case "dnLastMonth": {
      const last = monthRange(-1);
      const lastRows = await fetchDeliveryNotesLive({
        ...last,
        limit: 1000,
        maxPages: 1,
      });
      return {
        summary: {
          lastMonth: {
            label: last.label,
            items: lastRows.map(mapVehicleDeliveryNote),
            ...summarizeDeliveryNotes(lastRows),
          },
        },
      };
    }

    case "deliveryNoteSummary": {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      const from = new Date(2025, 0, 1).toISOString();
      const to = now.toISOString();
      const rows = await fetchDeliveryNotesLive({
        from,
        to,
        invoiceMade: [1, 0],
        limit: 1000,
        maxPages: 1,
      });
      return {
        summary: {
          label: "2025",
          from,
          to,
          ...summarizeDeliveryNotes(rows),
        },
      };
    }

    case "pendingDeliveryNotes": {
      const now = new Date();
      now.setHours(23, 59, 59, 999);
      const pending = await fetchDeliveryNotesLive({
        from: new Date(2025, 0, 1).toISOString(),
        to: now.toISOString(),
        invoiceMade: 0,
        limit: 1000,
        maxPages: 1,
      });
      return {
        list: pending.map(mapVehicleDeliveryNote),
        summary: summarizeDeliveryNotes(pending),
      };
    }

    default:
      return null;
  }
}
